import React, { useCallback, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import api from "../../services/api";

// Projeto "Folha de Solte" (07/10/2026, pedido do usuário) - mesmo molde do
// PixSheetModal/index.js (folha branca Arial 12px, linha de 26px com risco #8fb3dd,
// selo vermelho girado, mesma janela de impressão), só que pro lado do empréstimo
// SOLTO (dinheiro saindo). "regiao" = "interior" ou "capital".
const LINHAS_POR_FOLHA = 32;

// 29/10/2026 (pedido do usuário): Interior e Capital viraram uma folha só - "capital" é a
// chave que continua sendo usada no backend (ver SolteSheetService.ts), mas o título na
// tela não fala mais em região nenhuma.
const TITULOS = { interior: "Folha de Solte", capital: "Folha de Solte" };

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const linhasComVazias = (folha) => Array.from({ length: LINHAS_POR_FOLHA }, (_, i) => folha.linhas[i] || null);

const folhaHtml = (folha, regiao) => {
  const corpo = linhasComVazias(folha)
    .map((p) =>
      p
        ? `<div class="row"><span>${esc(p.dataTxt)}</span><span>${esc(p.nome)}${p.cidade ? ` - ${esc(p.cidade)}` : ""}</span><span>${esc(p.tipoCliente)}</span><span>${esc(p.cpf)}</span><span class="v">${esc(p.valor)}</span><span class="v">${esc(p.juros)}</span></div>`
        : `<div class="row"><span></span><span></span><span></span><span></span><span></span><span></span></div>`
    )
    .join("\n");
  const selo = folha.impressa ? `<div class="selo">FOLHA JÁ IMPRESSA</div>` : "";
  const titulo = TITULOS[regiao] || "Folhas de Solte";
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>${titulo} - Folha ${folha.numero}</title>
<style>
@page { size: A4; margin: 0; }
html, body { margin: 0; background: #fff; }
body { font-family: Arial, Helvetica, sans-serif; color: #111; }
.folha { width: 210mm; height: 297mm; box-sizing: border-box; padding: 12mm 14mm 10mm 14mm; position: relative; overflow: hidden; }
.titulo { height: 8mm; line-height: 8mm; font-size: 11pt; font-weight: 700; margin-left: 12mm; }
.cab, .row { display: grid; grid-template-columns: 14mm 1fr 22mm 28mm 22mm 18mm; height: 8mm; line-height: 8mm; margin-left: 12mm; border-bottom: 0.3mm solid #8fb3dd; font-size: 9pt; white-space: nowrap; overflow: hidden; }
.cab { font-weight: 700; border-bottom: 0.6mm solid #3d6fa8; text-transform: uppercase; }
.row span { overflow: hidden; text-overflow: ellipsis; padding-left: 2mm; }
.row .v { text-align: right; padding-right: 3mm; font-variant-numeric: tabular-nums; }
.folha::before { content: ""; position: absolute; top: 0; bottom: 0; left: 18mm; width: 0.4mm; background: #d9534f; }
.selo { position: absolute; top: 18mm; right: 14mm; border: 1.2mm solid #b91c1c; color: #b91c1c; font: 700 14pt Arial, sans-serif; padding: 2mm 4mm; transform: rotate(-8deg); letter-spacing: 0.5mm; }
</style></head>
<body><div class="folha">
<div class="titulo">${titulo} - Folha ${folha.numero}</div>
<div class="cab"><span>Data</span><span>Nome - cidade</span><span>Cliente</span><span>CPF</span><span style="text-align:right;padding-right:3mm">Valor</span><span style="text-align:right;padding-right:3mm">Juros</span></div>
${corpo}
${selo}
</div>
<script>
window.addEventListener('afterprint', function () {
  if (window.opener) window.opener.postMessage({ type: 'soltesheet-afterprint', regiao: '${regiao}', numero: ${folha.numero} }, '*');
});
window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 200); });
</script>
</body></html>`;
};

const SolteSheetModal = ({ open, regiao, onClose, onChanged }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [confirmar, setConfirmar] = useState(null);
  const [indice, setIndice] = useState(null);

  const carregar = useCallback(async () => {
    if (!regiao) return;
    try {
      const { data } = await api.get(`/solte-sheets/${regiao}`);
      setDados(data);
      setIndice((atual) => {
        const ultima = Math.max(0, data.folhas.length - 1);
        return atual === null ? ultima : Math.min(atual, ultima);
      });
      setErro("");
      if (onChanged) onChanged(data);
    } catch (_error) {
      setErro("Não deu pra carregar as folhas de Solte agora.");
    }
  }, [regiao, onChanged]);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  useEffect(() => {
    setIndice(null);
  }, [regiao]);

  useEffect(() => {
    const aoVoltarDaImpressao = (event) => {
      if (event?.data?.type !== "soltesheet-afterprint" || event.data.regiao !== regiao) return;
      setConfirmar({ numero: event.data.numero });
    };
    window.addEventListener("message", aoVoltarDaImpressao);
    return () => window.removeEventListener("message", aoVoltarDaImpressao);
  }, [regiao]);

  const imprimir = (folha) => {
    const janela = window.open("", "_blank");
    if (!janela) {
      setErro("O navegador bloqueou a janela de impressão. Libere pop-ups deste site.");
      return;
    }
    janela.document.open();
    janela.document.write(folhaHtml(folha, regiao));
    janela.document.close();
  };

  const confirmarImpressao = async () => {
    if (!confirmar) return;
    try {
      const { data } = await api.post(`/solte-sheets/${regiao}/printed`, { numero: confirmar.numero });
      setDados(data);
      if (onChanged) onChanged(data);
    } catch (_error) {
      setErro("Não deu pra marcar a folha como impressa. Tente de novo.");
    } finally {
      setConfirmar(null);
    }
  };

  const folhas = dados?.folhas || [];
  const atual = indice === null ? folhas.length - 1 : Math.min(indice, folhas.length - 1);
  const folha = folhas[atual];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{TITULOS[regiao] || "Folhas de Solte"}</DialogTitle>
        <DialogContent dividers>
          {erro && <Typography color="error">{erro}</Typography>}
          {dados && !folha && <Typography>Nenhum solte lançado ainda.</Typography>}
          {folha && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={atual <= 0}
                  onClick={() => setIndice(Math.max(0, atual - 1))}
                  aria-label="folha anterior"
                >
                  {"<"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={atual >= folhas.length - 1}
                  onClick={() => setIndice(Math.min(folhas.length - 1, atual + 1))}
                  aria-label="próxima folha"
                >
                  {">"}
                </Button>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Folha {folha.numero} de {folhas.length} ({folha.linhas.length}/{LINHAS_POR_FOLHA} Soltes)
                </Typography>
                {folha.impressa && (
                  <Typography variant="body2" style={{ color: "#b91c1c", fontWeight: 700 }}>
                    FOLHA JÁ IMPRESSA
                  </Typography>
                )}
                <Button size="small" variant="outlined" onClick={() => imprimir(folha)}>
                  Imprimir
                </Button>
              </div>
              <div
                style={{
                  width: 794,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  border: "1px solid #ccc",
                  background: "#fff",
                  color: "#111",
                  padding: "12px 14px",
                  fontFamily: "Arial, sans-serif",
                  position: "relative",
                  fontSize: 12,
                  overflowX: "auto"
                }}
              >
                {linhasComVazias(folha).map((p, i) => (
                  <div
                    key={p ? p.id : `vazia-${i}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "48px 1fr 92px 95px 80px 75px",
                      borderBottom: "1px solid #8fb3dd",
                      lineHeight: "26px",
                      height: 26,
                      whiteSpace: "nowrap",
                      overflow: "hidden"
                    }}
                  >
                    <span>{p ? p.dataTxt : ""}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p ? `${p.nome}${p.cidade ? ` - ${p.cidade}` : ""}` : ""}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: 11, color: "#555" }}>
                      {p ? p.tipoCliente : ""}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{p ? p.cpf : ""}</span>
                    <span style={{ textAlign: "right" }}>{p ? p.valor : ""}</span>
                    <span style={{ textAlign: "right" }}>{p ? p.juros : ""}</span>
                  </div>
                ))}
                {folha.impressa && (
                  <div
                    style={{
                      position: "absolute",
                      top: 18,
                      right: 18,
                      border: "3px solid #b91c1c",
                      color: "#b91c1c",
                      fontWeight: 700,
                      padding: "4px 10px",
                      transform: "rotate(-8deg)",
                      background: "rgba(255,255,255,0.85)"
                    }}
                  >
                    FOLHA JÁ IMPRESSA
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)}>
        <DialogTitle>A folha saiu da impressora?</DialogTitle>
        <DialogContent>
          <Typography>
            Só marco a folha como impressa se ela realmente foi impressa. Se você cancelou, responda Não.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)}>Não</Button>
          <Button color="primary" variant="contained" onClick={confirmarImpressao}>
            Sim, foi impressa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SolteSheetModal;
